import React, { useEffect, useState } from 'react';
import { useTypedTranslation } from '@app/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { CompanyLogo, PasslyButton, ButtonSubmit, PasslyCheckbox } from '@app/components';
import { setExceptionHandler, setRedirectAfterSignin } from '@app/store/actions';
import { authService, basicService } from '@app/services';
import { genericErrorToast, interpolateTranslation, sessionStore, getUrlParam } from '@app/helpers';
import {
    IAuthorizeResponse,
    IAuthorizeScope,
    IGlobalState,
    IAuthorizeApproved,
    IAuthorizeApprovedResponse,
    SigninResponseTypes,
    SessionStorageTypes,
    IShellConfig
} from '@app/models';
import { Paper } from '@material-ui/core';
import { VerifiedUser } from '@material-ui/icons';
import { useLocation } from 'react-router';
import { AxiosResponse, AxiosError } from 'axios';

const Authorize = () => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();
    const location = useLocation();

    const [authorization, setAuthorization] = useState<IAuthorizeResponse>();
    const [rememberDecision, shouldRememberDecision] = useState<boolean>(false);
    const [interpolation, setInterpolation] = useState<string[]>([]);
    const [validRequest, isRequestValid] = useState<boolean>(false);
    const [allowLoading, isAllowLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const shellLoading: boolean = useSelector((state: IGlobalState) => state.basic.shellConfigLoading);
    const shellConfig: IShellConfig = useSelector((state: IGlobalState) => state.basic.shellConfig);

    useEffect(() => {
        let search = location.search;

        dispatch(setExceptionHandler(true));
        dispatch(setRedirectAfterSignin(location.pathname + search.replace(/[\&]prompt=[^&]+/, '')));
    }, []);

    useEffect(() => {
        const fullPath = location.pathname + location.search;
        const promptParam = getUrlParam(location.pathname + location.search, 'prompt');

        if (promptParam === 'login') {
            basicService.redirect('/signin');
            return;
        } else if (promptParam === 'none' && !shellLoading && !shellConfig.authActive) {
            const redirectLocation = getUrlParam(fullPath, 'redirect_uri');
            const state = getUrlParam(fullPath, 'state');
            let redirectUrl = `${redirectLocation}?error=login_required`;

            if (state !== '') {
                redirectUrl += `&state=${state}`;
            }

            window.location.href = redirectUrl;
            return;
        }

        if (!shellLoading) authService.authorize(location.search, handleAuthorize, handleAuthorizeError);
    }, [shellLoading]);

    const handleAuthorize = (response: AxiosResponse): void => {
        if (response.data.response) {
            processApprovalResponse(response);
        } else {
            processAuthResponse(response);
        }
    };

    const handleDecision = (decision: boolean) => () => {
        shouldRememberDecision(decision);
    };

    const processAuthResponse = (response: AxiosResponse<IAuthorizeResponse>): void => {
        const { data } = response;
        if (data.redirect) {
            window.location.href = data.redirect;
        }
        setAuthorization(data);
        setInterpolation([data.clientDisplayName, data.applicationDisplayName]);
        isRequestValid(true);
    };

    const processApprovalResponse = (response: AxiosResponse<IAuthorizeApproved>) => {
        const { data } = response;
        sessionStore.removeItem(SessionStorageTypes.aaSid);

        if (!data || !data.response || !data.response.approved) {
            window.location.href = data.redirect;
            return;
        }

        switch (data.response.type) {
            case SigninResponseTypes.id_token:
                handleImplicit(data.response);
                break;
            case SigninResponseTypes.token:
            case SigninResponseTypes.code:
                window.location.href = data.redirect;
                break;
            case SigninResponseTypes.postMessage:
                postMessage(data.response);
                break;
        }

        isAllowLoading(false);
    };

    const handleImplicit = (authResponse: IAuthorizeApprovedResponse) => {
        let params: any = {
            id_token: authResponse.idToken,
            state: authResponse.state
        };

        let form = (document as any).querySelector('#oidcForm');

        form.action = authResponse.redirectUri;

        for (let k in params) {
            let hidden = document.createElement('input');

            hidden.type = 'hidden';
            hidden.name = k;
            hidden.value = params[k];

            form.appendChild(hidden);
        }

        setTimeout(function () {
            form.submit();
        }, 1000);
    };

    const postMessage = (response: IAuthorizeApprovedResponse) => {
        if (window.parent) {
            window.parent.postMessage({ token: response.accessToken }, response.origin);
        }
    };

    const denyAuth = () => {
        authService.authorizeApproval(
            location.search,
            {
                approved: false,
                rememberDecision: false
            },
            (response: AxiosResponse) => processApprovalResponse(response),
            genericErrorToast
        );
    };

    const handleAuthorizeError = (error: AxiosError<any>) => {
        try {
            setErrorMessage(error.response?.data.message);
        } catch {
            genericErrorToast(error);
        }
    };

    const allowAuth = () => {
        isAllowLoading(true);

        const handleError = (error: AxiosError<any>) => {
            isAllowLoading(false);
            genericErrorToast(error);
        };

        authService.authorizeApproval(
            location.search,
            {
                approved: true,
                rememberDecision: rememberDecision
            },
            (response: AxiosResponse) => processApprovalResponse(response),
            (error: AxiosError<any>) => handleError(error)
        );
    };

    return (
        <div className='forgot-password'>
            <CompanyLogo />
            {!shellLoading && errorMessage === '' && <h1>{translation('AUTHORIZE_HEADER')}</h1>}
            {errorMessage !== '' && <h1>{errorMessage}</h1>}
            <Paper elevation={2} square className='authorize' hidden={!validRequest}>
                <section>
                    <div>
                        <div className='authorize__header'>
                            <div className='authorize__logo'>{authorization?.applicationIcon && <img src={authorization?.applicationIcon} />}</div>

                            <div className='authorize__details'>
                                <span>{interpolateTranslation(translation('PERMISSIONS_REQUEST_INTER'), interpolation)}</span>
                            </div>
                        </div>

                        <ul className='authorize__scopes'>
                            {authorization?.scopes.map((scope: IAuthorizeScope, i: number) => (
                                <li key={i}>
                                    <Paper elevation={1} className='authorize__scope'>
                                        <div className='authorize__scope-icon'>
                                            <VerifiedUser />
                                        </div>
                                        <div className='authorize__scope-description'>
                                            <span>
                                                <b>{translation(scope.displayName)}</b>
                                            </span>
                                            <span>{translation(scope.description)}</span>
                                        </div>
                                    </Paper>
                                </li>
                            ))}
                        </ul>

                        <div className='authorize__footer'>
                            {authorization?.showRememberConsent && (
                                <div className='authorize__consent'>
                                    <PasslyCheckbox
                                        name='rememberconsent'
                                        checked={rememberDecision}
                                        onChange={handleDecision(!rememberDecision)}
                                        label={translation('REMEMBER_AUTHORIZE_CONSENT')}
                                    />
                                </div>
                            )}

                            <div className='authorize__actions'>
                                <div className='authorize__buttons'>
                                    <PasslyButton id='deny' variant='outlined' onClick={denyAuth}>
                                        {translation('DENY_AUTH_BUTTON')}
                                    </PasslyButton>
                                    <ButtonSubmit
                                        buttonText={translation('ALLOW_AUTH_BUTTON')}
                                        isDisabled={allowLoading}
                                        isLoad={allowLoading}
                                        id='allow'
                                        onClick={allowAuth}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </Paper>

            <form id='oidcForm' method='post'></form>
        </div>
    );
};

export default Authorize;
